import { ServiceRequest } from "common";
import { Formik } from "formik";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Dialog, Label, Select, YStack, Text, Spinner } from "tamagui";

export interface FormValues {
  status: string;
}

interface ServiceRequestChangeStatusModalProps {
  open: boolean;
  serviceRequest: ServiceRequest | null;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: FormValues) => Promise<void>;
}

export function ServiceRequestChangeStatusModal({
  open,
  serviceRequest,
  onOpenChange,
  onSubmit,
}: ServiceRequestChangeStatusModalProps) {
  const { t } = useTranslation();
  const [loading, setIsLoading] = useState(false);

  // Define all possible status options
  const allStatusOptions = [
    { value: "pending", label: t("serviceRequests.statuses.pending") },
    { value: "canceled", label: t("serviceRequests.statuses.canceled") },
    { value: "reschedule", label: t("serviceRequests.statuses.reschedule") },
    { value: "open", label: t("serviceRequests.statuses.open") },
  ];

  // Filter out the current status from available options
  const availableStatusOptions = allStatusOptions.filter(
    (option) => option.value !== serviceRequest?.status,
  );

  const initialValues: FormValues = {
    status: availableStatusOptions[0].value,
  };

  const handleSubmit = async (values: FormValues) => {
    setIsLoading(true);
    await onSubmit(values);
    onOpenChange(false);
    setIsLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Trigger asChild />
      <Dialog.Portal zIndex={1}>
        <Dialog.Overlay />
        <Dialog.Content
          bordered
          elevate
          size="$8"
          padding="$4"
          width="480px"
          maxHeight="90%"
        >
          <Dialog.Title fontSize="$3" lineHeight="$3">
            Edit Status
          </Dialog.Title>
          <Formik
            initialValues={initialValues}
            validationSchema={undefined}
            onSubmit={handleSubmit}
            enableReinitialize
            validateOnBlur={false}
            validateOnChange={false}
          >
            {({ handleChange, handleSubmit, values }) => (
              <YStack space="$2" marginTop="$2">
                <YStack>
                  <Label>{t("serviceRequests.statusModal.status")}</Label>
                  <Select
                    value={values.status}
                    onValueChange={handleChange("status")}
                  >
                    <Select.Trigger>
                      <Select.Value
                        placeholder={t(
                          "serviceRequests.statusModal.selectStatus",
                        )}
                        fontSize="$4"
                      />
                    </Select.Trigger>
                    <Select.Content>
                      <Select.ScrollUpButton />
                      <Select.Viewport>
                        {availableStatusOptions.map((option, index) => (
                          <Select.Item
                            key={option.value}
                            value={option.value}
                            index={index}
                            hoverStyle={{ backgroundColor: "$white7" }}
                            pressStyle={{ backgroundColor: "$white7" }}
                          >
                            <Select.ItemText>{option.label}</Select.ItemText>
                          </Select.Item>
                        ))}
                      </Select.Viewport>
                    </Select.Content>
                  </Select>
                </YStack>

                <YStack
                  space="$2"
                  marginTop="$4"
                  flexDirection="row"
                  justifyContent="flex-end"
                >
                  <Button
                    onPress={() => onOpenChange(false)}
                    color={"$color12"}
                  >
                    {t("common.cancel")}
                  </Button>
                  <Button
                    onPress={() => handleSubmit()}
                    backgroundColor={"$hgPrimary"}
                    disabled={loading}
                  >
                    {loading ? (
                      <Spinner color="white" size="small" />
                    ) : (
                      <Text>{t("common.submit")}</Text>
                    )}
                  </Button>
                </YStack>
              </YStack>
            )}
          </Formik>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  );
}

export default ServiceRequestChangeStatusModal;
